import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, ShoppingCartSimple, Cpu, Desktop, Monitor, DeviceMobileSpeaker, HardDrives, CheckCircle } from "@phosphor-icons/react";

export const fmtMoney = (n, currency = "MXN") =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency, maximumFractionDigits: 0 }).format(n || 0);

const CONDITION = { nuevo: "Nuevo", seminuevo: "Seminuevo", reacondicionado: "Reacondicionado", usado: "Usado" };

// Tarjeta de producto del home (carrusel de ofertas y catálogo con filtros).
export default function HomeProductCard({ product, wished, onWish, onAdd, onView, className = "shrink-0 w-[260px] snap-start", testid = "offer" }) {
  const p = product;
  const currency = p.sale_currency || "MXN";
  const store = Number(p.store_price || 0);
  const cmp = Number(p.compare_at_price || 0);
  const hasDiscount = cmp > 0 && cmp > store;
  const discountPct = hasDiscount ? Math.round((1 - store / cmp) * 100) : 0;
  const specRows = useMemo(() => {
    const rows = [];
    const s = p.specs || {};
    if (s.cpu) rows.push({ Icon: Cpu, text: s.cpu });
    if (s.ram) rows.push({ Icon: HardDrives, text: `RAM ${s.ram}` });
    if (s.storage) rows.push({ Icon: Desktop, text: s.storage });
    if (s.screen) rows.push({ Icon: Monitor, text: s.screen });
    if (p.condition && CONDITION[p.condition]) rows.push({ Icon: CheckCircle, text: CONDITION[p.condition] });
    return rows.slice(0, 5);
  }, [p.specs, p.condition]);
  const photo = (p.photos || [])[0] || p.photos_url;
  const [imgOk, setImgOk] = useState(true);

  return (
    <div className={`${className} bg-white border border-zinc-200 rounded-xl p-3 flex flex-col hover:shadow-md hover:border-[#002FA7]/40 transition-[box-shadow,border-color] relative`} data-testid={`${testid}-${p.id}`}>
      {hasDiscount && (
        <span className="absolute top-2 left-2 z-10 bg-[#E0000F] text-white text-[11px] font-bold px-2 py-0.5 rounded">-{discountPct}%</span>
      )}
      <button onClick={onWish} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 border border-zinc-200 flex items-center justify-center hover:border-[#E0000F] transition-colors z-10" aria-label="Guardar en favoritos">
        <Heart size={14} weight={wished ? "fill" : "regular"} className={wished ? "text-[#E0000F]" : "text-zinc-500"} />
      </button>
      <Link to={`/catalogo/p/${p.id}`} className="block">
        <div className="aspect-square bg-zinc-50 rounded-lg overflow-hidden flex items-center justify-center">
          {photo && imgOk ? <img src={photo} alt={p.name} onError={() => setImgOk(false)} className="w-full h-full object-contain p-2" loading="lazy" /> : <DeviceMobileSpeaker size={48} className="text-zinc-300" />}
        </div>
      </Link>
      <div className="mt-3 space-y-1">
        {p.brand && <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold">{p.brand}</div>}
        <Link to={`/catalogo/p/${p.id}`} className="block font-heading font-bold text-sm leading-tight line-clamp-2 hover:text-[#002FA7]">{p.name}</Link>
        {p.short_tagline && <div className="text-[11px] text-zinc-500 italic line-clamp-1">{p.short_tagline}</div>}
      </div>
      {specRows.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-x-2 gap-y-0.5">
          {specRows.map((r, i) => (
            <li key={i} className="flex items-center gap-1 text-[11px] text-zinc-600 max-w-full">
              <r.Icon size={11} weight="bold" className="text-[#002FA7] shrink-0" />
              <span className="truncate">{r.text}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-auto pt-3">
        {hasDiscount && <div className="text-[11px] text-zinc-400 line-through">{fmtMoney(cmp, currency)}</div>}
        <div className={`font-heading font-extrabold text-lg ${hasDiscount ? "text-[#E0000F]" : "text-zinc-900"}`}>{fmtMoney(store, currency)}</div>
        <div className="mt-2 grid gap-1.5">
          {onView ? (
            <button type="button" onClick={onView} className="inline-flex items-center justify-center text-xs font-bold text-[#002FA7] border border-[#002FA7]/30 hover:bg-[#002FA7]/5 py-2 rounded-full transition-colors" data-testid={`${testid}-view-${p.id}`}>Ver equipo</button>
          ) : (
            <Link to={`/catalogo/p/${p.id}`} className="inline-flex items-center justify-center text-xs font-bold text-[#002FA7] border border-[#002FA7]/30 hover:bg-[#002FA7]/5 py-2 rounded-full transition-colors" data-testid={`${testid}-view-${p.id}`}>Ver equipo</Link>
          )}
          <button onClick={onAdd} disabled={!p.max_qty} className="inline-flex items-center justify-center gap-1 bg-[#002FA7] hover:bg-[#0043c2] disabled:bg-zinc-300 text-white text-xs font-bold py-2 rounded-full transition-colors" data-testid={`${testid}-add-${p.id}`}>
            <ShoppingCartSimple size={13} weight="bold" /> {p.max_qty ? "Agregar al carrito" : "Agotado"}
          </button>
        </div>
      </div>
    </div>
  );
}
